"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { defaultTemplateFor, TEMPLATES } from "@/lib/resumeRender/catalog";
import type { ResumeSample } from "@/lib/resumeRender/model";
import { trackEvent } from "@/lib/analytics/track";
import ResumePreview from "./ResumePreview";

// Live resume previews for a job title's samples, a full-size viewer with a
// template picker, in-browser PDF downloads and a way into the resume builder
// with the sample filled in. Sample cards elsewhere on
// the page open the viewer through a window event, so they can stay
// server-rendered. Styled like the home page (black pills, white cards).

const OPEN_EVENT = "nohaya:resume-preview";

type OpenDetail = { sampleId: number; templateId?: string };

type Shared = { slug: string; jobTitle: string; year: number };

const PILL_DARK =
  "inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0b0f19] px-5 py-2.5 text-xs font-semibold text-white shadow-[0_10px_24px_-12px_rgba(15,23,42,0.7)] transition hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-70 dark:bg-white dark:text-[#0b0f19]";
const PILL_LIGHT =
  "inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-xs font-semibold text-slate-800 ring-1 ring-slate-200 transition hover:ring-slate-400 disabled:opacity-70 dark:bg-white/10 dark:text-white dark:ring-white/15";

function fileName(slug: string, sample: ResumeSample, templateId: string) {
  return `${slug}-resume-sample-${sample.resume_variant_no}-${templateId}.pdf`;
}

/** Opens the resume builder with a new resume filled from [sample] in [templateId]. */
function builderHref(slug: string, sample: ResumeSample, templateId: string) {
  return `/resume-builder/edit?slug=${encodeURIComponent(slug)}&variant=${sample.resume_variant_no}&template=${encodeURIComponent(templateId)}`;
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function openViewer(detail: OpenDetail) {
  window.dispatchEvent(new CustomEvent<OpenDetail>(OPEN_EVENT, { detail }));
}

/** Download state for one button. */
function useDownload(shared: Shared) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const download = useCallback(
    async (sample: ResumeSample, templateId: string) => {
      setBusy(true);
      setFailed(false);
      trackEvent("resume_pdf_download", { slug: shared.slug, resumeId: sample.id, template: templateId });
      try {
        const { downloadResumePdf } = await import("./downloadResumePdf");
        await downloadResumePdf({
          sample,
          templateId,
          year: shared.year,
          jobTitle: shared.jobTitle,
          fileName: fileName(shared.slug, sample, templateId),
        });
      } catch (err) {
        console.error(err);
        setFailed(true);
      } finally {
        setBusy(false);
      }
    },
    [shared.slug, shared.jobTitle, shared.year]
  );
  return { busy, failed, download };
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
      <path d="M12 4v11m0 0-4.5-4.5M12 15l4.5-4.5M5 19h14" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Spinner() {
  return <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />;
}

function accentHex(argb: number) {
  return `#${(argb & 0xffffff).toString(16).padStart(6, "0")}`;
}

export function SampleGallery({ samples, ...shared }: Shared & { samples: ResumeSample[] }) {
  const [open, setOpen] = useState<OpenDetail | null>(null);

  useEffect(() => {
    const onOpen = (e: Event) => setOpen((e as CustomEvent<OpenDetail>).detail);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const current = open ? samples.find((s) => s.id === open.sampleId) : null;

  return (
    <>
      <div className="-mx-4 overflow-x-auto px-4 pb-6 pt-1 [scrollbar-width:thin] sm:-mx-6 sm:px-6">
        <ul className="flex snap-x gap-4">
          {samples.map((sample) => {
            const template = defaultTemplateFor(sample.resume_variant_no);
            return (
              <li key={sample.id} className="w-[188px] flex-shrink-0 snap-start">
                <button
                  type="button"
                  onClick={() => setOpen({ sampleId: sample.id })}
                  className="group block w-full rounded-[1.25rem] bg-white p-2 text-left shadow-[0_14px_34px_-22px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/70 transition hover:-translate-y-1 hover:shadow-[0_22px_44px_-22px_rgba(15,23,42,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0b0f19] dark:bg-white/[0.04] dark:ring-white/10"
                >
                  <ResumePreview
                    sample={sample}
                    templateId={template.info.id}
                    year={shared.year}
                    width={172}
                    lazy
                    className="rounded-xl ring-1 ring-black/5"
                    label={`${shared.jobTitle} resume sample ${sample.resume_variant_no} in the ${template.info.name} template`}
                  />
                  <span className="mt-2.5 flex items-center gap-1.5 px-1 text-sm font-semibold text-[#0b0f19] dark:text-white">
                    <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: accentHex(template.info.defaultAccent) }} />
                    {template.info.name}
                  </span>
                  <span className="block px-1 pb-1 text-[11px] text-slate-500 dark:text-slate-400">
                    Sample {sample.resume_variant_no} · {template.info.layout}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      {current ? (
        <SampleViewer
          key={`${current.id}-${open?.templateId ?? ""}`}
          sample={current}
          initialTemplate={open?.templateId ?? defaultTemplateFor(current.resume_variant_no).info.id}
          onClose={() => setOpen(null)}
          {...shared}
        />
      ) : null}
    </>
  );
}

function SampleViewer({
  sample,
  initialTemplate,
  onClose,
  ...shared
}: Shared & { sample: ResumeSample; initialTemplate: string; onClose: () => void }) {
  const [templateId, setTemplateId] = useState(initialTemplate);
  const { busy, failed, download } = useDownload(shared);
  const closeRef = useRef<HTMLButtonElement>(null);
  const template = TEMPLATES.find((t) => t.info.id === templateId) ?? TEMPLATES[0];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${shared.jobTitle} resume sample ${sample.resume_variant_no}`}
      className="fixed inset-0 z-[100] flex items-stretch justify-center bg-[#0b0f19]/60 backdrop-blur-sm sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="flex w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl sm:rounded-[2rem] lg:flex-row dark:bg-[#0f172a]">
        <div className="min-h-0 flex-1 overflow-y-auto bg-[#f1f3f9] p-4 sm:p-8 dark:bg-[#0b1220]">
          <ResumePreview
            sample={sample}
            templateId={templateId}
            year={shared.year}
            full
            className="mx-auto max-w-[720px] rounded-md shadow-[0_30px_60px_-30px_rgba(15,23,42,0.45)]"
            label={`${shared.jobTitle} resume sample in the ${template.info.name} template`}
          />
        </div>
        <aside className="flex max-h-[45vh] flex-shrink-0 flex-col border-t border-slate-200 lg:max-h-none lg:w-80 lg:border-l lg:border-t-0 dark:border-white/10">
          <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-5 dark:border-white/10">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Sample {sample.resume_variant_no}
              </p>
              <h2 className="mt-1 text-lg font-bold tracking-tight text-[#0b0f19] dark:text-white">{shared.jobTitle} Resume</h2>
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#0b0f19] text-white transition hover:scale-105 dark:bg-white dark:text-[#0b0f19]"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">Template</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {TEMPLATES.map((t) => {
                const active = t.info.id === templateId;
                return (
                  <button
                    key={t.info.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setTemplateId(t.info.id)}
                    className={`rounded-2xl px-3 py-2.5 text-left transition ${
                      active
                        ? "bg-[#0b0f19] text-white dark:bg-white dark:text-[#0b0f19]"
                        : "bg-slate-50 text-[#0b0f19] ring-1 ring-slate-200/80 hover:ring-slate-400 dark:bg-white/5 dark:text-white dark:ring-white/10"
                    }`}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-semibold">
                      <span aria-hidden="true" className="h-2.5 w-2.5 flex-shrink-0 rounded-full ring-2 ring-white/40" style={{ background: accentHex(t.info.defaultAccent) }} />
                      {t.info.name}
                    </span>
                    <span className={`mt-0.5 block text-[11px] ${active ? "text-white/70 dark:text-slate-500" : "text-slate-500 dark:text-slate-400"}`}>
                      {t.info.layout}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{template.info.tagline}.</p>
          </div>
          <div className="border-t border-slate-200 p-5 dark:border-white/10">
            <button type="button" disabled={busy} onClick={() => download(sample, templateId)} className={`${PILL_DARK} py-3 text-sm`}>
              {busy ? <Spinner /> : <DownloadIcon />}
              {busy ? "Preparing PDF…" : "Download PDF"}
            </button>
            <Link href={builderHref(shared.slug, sample, templateId)} rel="nofollow" className={`${PILL_LIGHT} mt-2 py-3 text-sm`}>
              <EditIcon />
              Edit in resume builder
            </Link>
            {failed ? (
              <p className="mt-2 text-center text-xs text-rose-600 dark:text-rose-400">Couldn&apos;t create the PDF. Please try again.</p>
            ) : (
              <p className="mt-2 text-center text-[11px] text-slate-400 dark:text-slate-500">Free · A4 · made in your browser</p>
            )}
          </div>
        </aside>
      </div>
    </div>,
    document.body
  );
}

/**
 * A sample card's preview: the sample drawn in a randomly picked template
 * (chosen in the browser after load, so every visit shows some variety and
 * cached HTML stays identical), with Preview/Download for that template.
 */
export function SampleCardPreview({ sample, ...shared }: Shared & { sample: ResumeSample }) {
  const { busy, failed, download } = useDownload(shared);
  const [templateId, setTemplateId] = useState<string | null>(null);
  useEffect(() => {
    // Random per visit by design; set after mount so server and client HTML match.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTemplateId(TEMPLATES[Math.floor(Math.random() * TEMPLATES.length)].info.id);
  }, []);
  const chosen = templateId ?? defaultTemplateFor(sample.resume_variant_no).info.id;
  const template = TEMPLATES.find((t) => t.info.id === chosen) ?? TEMPLATES[0];

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => openViewer({ sampleId: sample.id, templateId: chosen })}
        aria-label={`Open ${shared.jobTitle} sample ${sample.resume_variant_no} in the ${template.info.name} template`}
        className="group block w-full overflow-hidden rounded-xl shadow-[0_18px_40px_-24px_rgba(15,23,42,0.5)] ring-1 ring-slate-200/80 transition hover:-translate-y-0.5 hover:ring-slate-400 dark:ring-white/10"
      >
        {templateId ? (
          <ResumePreview sample={sample} templateId={chosen} year={shared.year} width={208} lazy label={`${shared.jobTitle} resume sample`} />
        ) : (
          <div aria-hidden="true" className="aspect-[595/842] w-full bg-white" />
        )}
      </button>
      <p className="flex items-center justify-center gap-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">
        <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: accentHex(template.info.defaultAccent) }} />
        {templateId ? `${template.info.name} template` : "Loading preview…"}
      </p>
      <button type="button" onClick={() => openViewer({ sampleId: sample.id, templateId: chosen })} className={PILL_DARK}>
        Preview &amp; change template
      </button>
      <button type="button" disabled={busy || !templateId} onClick={() => download(sample, chosen)} className={PILL_LIGHT}>
        {busy ? <Spinner /> : <DownloadIcon />}
        {busy ? "Preparing PDF…" : "Download PDF"}
      </button>
      {failed ? <p className="text-center text-[11px] text-rose-600 dark:text-rose-400">Couldn&apos;t create the PDF.</p> : null}
      <Link
        href={builderHref(shared.slug, sample, chosen)}
        rel="nofollow"
        className="flex items-center justify-center gap-1.5 text-[11px] font-semibold text-slate-500 transition hover:text-[#0b0f19] dark:text-slate-400 dark:hover:text-white"
      >
        <EditIcon />
        Edit in resume builder
      </Link>
    </div>
  );
}

/** Wraps any content in a button that opens the page's viewer on [sampleId]. */
export function SampleOpener({
  sampleId,
  className,
  label,
  children,
}: {
  sampleId: number;
  className?: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button type="button" aria-label={label} className={className} onClick={() => openViewer({ sampleId })}>
      {children}
    </button>
  );
}

/** A single static preview (featured cards, hero). */
export function SamplePreview(props: {
  sample: ResumeSample;
  year: number;
  width?: number;
  lazy?: boolean;
  className?: string;
  label?: string;
  templateId?: string;
}) {
  const templateId = props.templateId ?? defaultTemplateFor(props.sample.resume_variant_no).info.id;
  return (
    <ResumePreview
      sample={props.sample}
      templateId={templateId}
      year={props.year}
      width={props.width}
      lazy={props.lazy}
      className={props.className}
      label={props.label}
    />
  );
}
